import * as React from 'react';
import { useCallback, useEffect } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';

import { cn, useResponsiveBreakPoints, useAmbassadorData } from '@restworld/utils-common';
import { useAuth } from '@restworld/feature-authentication';
import { Select, SelectOptionType, Spinner } from '@restworld/ui-ds-base';

import {
  explorerRestaurantListLoadingAtom,
  explorerRestaurantMapLoadingAtom,
  explorerRestaurantHitsAtom,
  explorerRestaurantListPayloadAtom,
  isRestaurantsExplorerAtom,
  toggleActiveExplorerTypeAtom,
  ExplorerMapState,
} from './job-explorer-map';
import MobileDrawerElement from './mobile-drawer-element';
import JobExplorerDesktopElement from './job-explorer-desktop-element';
import { WorkerHomeLocationWrapper } from './job-explorer-map/worker-home-location';
import { jobLocationCoordinatesLoadingAtom } from './job-explorer-map.atoms';
import JobExplorerSaveResearchV2 from './job-explorer-map/job-explorer-save-research-v2/job-explorer-save-research-v2';
import { useAutoOpenSaveResearchModal } from './job-explorer-map/job-explorer-save-research-v2/use-auto-open-save-research-modal';
import {
  jobLocationPositionListLoadingAtom,
  jobPositionTotalCountAtom,
  hasMapViewAtom,
  jobSortFilterAtom,
  jobListParamsAtom,
} from './job-explorer-list.atoms';
import { useJobPositionCoordinates, useRestaurantLocationCoordinates } from './job-explorer.hooks';
import { useJobExplorerSortMenuList } from './job-explorer-sort-dropdown';
import { AmbassadorButton, useShortUrl } from '../legacy';

export const JobExplorerContent = () => {
  const { isDesktop } = useResponsiveBreakPoints();
  const ambassadorData = useAmbassadorData();
  const explorerLoading = useExplorerLoading();

  useAutoOpenSaveResearchModal();
  useFetchRestaurantExplorerParams();

  useJobPositionCoordinates();
  useRestaurantLocationCoordinates();

  return (
    <div className="relative" id="map-and-list" data-cy="explorer-map-and-list">
      <JobExplorerDesktopElement ambassadorCode={ambassadorData?.ambassador_code} />
      {!isDesktop && <MobileDrawerElement ambassadorCode={ambassadorData?.ambassador_code} />}
      {explorerLoading && <MapSpinner />}
      <JobExplorerSaveResearchV2 />
      <WorkerHomeLocationWrapper />
    </div>
  );
};
export default JobExplorerContent;

export const ListHeadTitle = React.memo(({ hideDropdown }: { hideDropdown?: boolean }) => {
  const isRestaurantsExplorer = useAtomValue(isRestaurantsExplorerAtom);
  const setJobListParams = useSetAtom(jobListParamsAtom);
  const [sortFilter, setSortFilter] = useAtom(jobSortFilterAtom);
  const hasMapView = useAtomValue(hasMapViewAtom);
  const setExplorerRestaurantListPayload = useSetAtom(explorerRestaurantListPayloadAtom);

  const menuList = useJobExplorerSortMenuList();

  const handleSortChange = useCallback(
    (value: SelectOptionType) => {
      setSortFilter(value.value);
      setJobListParams((prev) => ({
        ...prev,
        offset: 0,
        ...JSON.parse(value.value),
      }));
      setExplorerRestaurantListPayload((prev) => ({
        ...prev,
        offset: 0,
        ...JSON.parse(value.value),
      }));
    },
    [setExplorerRestaurantListPayload, setJobListParams, setSortFilter]
  );

  const selectOptions = React.useMemo(() => menuList.map((v) => ({ ...v, value: v.key })), [menuList]);
  const selectValue = React.useMemo(
    () => ({
      value: sortFilter,
      label: menuList.find((v) => v.key === sortFilter)?.label,
    }),
    [menuList, sortFilter]
  );

  return (
    <ListHeadTitleWrapper hasMapView={hasMapView}>
      <TotalCountTitle />
      {!hideDropdown && (
        <Select
          options={selectOptions}
          value={selectValue}
          onChange={handleSortChange}
          disabled={isRestaurantsExplorer}
        />
      )}
    </ListHeadTitleWrapper>
  );
});

const TotalCountTitle = React.memo(() => {
  const { t } = useTranslation('common');
  const totalJobs = useAtomValue(jobPositionTotalCountAtom);
  const explorerRestaurantHits = useAtomValue(explorerRestaurantHitsAtom);
  const isRestaurantsExplorer = useAtomValue(isRestaurantsExplorerAtom);
  const count = isRestaurantsExplorer ? explorerRestaurantHits : totalJobs ?? 0;
  return <h5 className="text-base font-medium min-w-[95px]">{`${count} ${t('results')}`}</h5>;
});

export const MapListTogglers = () => {
  const { loading, shortUrl: ambassadorShortUrl, getShortenURL } = useShortUrl();
  const ambassadorData = useAmbassadorData();

  // Gated on the ambassador record, not on `user.type`: somebody who is both a
  // worker and an ambassador has type "worker" (otherwise they could not apply
  // to job positions), and gating on the type hid this button from exactly
  // those people.
  if (!ambassadorData) return null;

  return (
    <AmbassadorButton
      type={ambassadorShortUrl === 'error' ? 'error' : 'success'}
      regenerateURL={() => getShortenURL(ambassadorData?.ambassador_code)}
      url={ambassadorShortUrl}
      loading={loading}
    />
  );
};

const MapSpinner = () => {
  const hasMapView = useAtomValue(hasMapViewAtom);
  return (
    <MapSpinnerWrapper data-cy="job-explorer-spinner" hasMapView={hasMapView}>
      <Spinner color="primary" size="sm" />
    </MapSpinnerWrapper>
  );
};

const MapSpinnerWrapper = ({
  hasMapView,
  className,
  ...props
}: { hasMapView?: boolean } & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'fixed bottom-[6%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 w-[88px] h-[48px] flex justify-center items-center bg-white border rounded-lg border-gray-200',
      hasMapView && 'bottom-[4%] lg:hidden',
      className
    )}
    {...props}
  />
);

export const useExplorerLoading = () => {
  const isRestaurantsExplorer = useAtomValue(isRestaurantsExplorerAtom);
  const [explorerLoading, setExplorerLoading] = React.useState(false);
  const explorerRestaurantListFetchLoading = useAtomValue(explorerRestaurantListLoadingAtom);
  const restaurantLocationCoordinatesFetchLoading = useAtomValue(explorerRestaurantMapLoadingAtom);
  const jobLocationPositionListLoading = useAtomValue(jobLocationPositionListLoadingAtom);
  const jobLocationCoordinatesLoading = useAtomValue(jobLocationCoordinatesLoadingAtom);

  const shouldLoad = React.useMemo(
    () =>
      (!isRestaurantsExplorer && (jobLocationPositionListLoading || jobLocationCoordinatesLoading)) ||
      (isRestaurantsExplorer && (explorerRestaurantListFetchLoading || restaurantLocationCoordinatesFetchLoading)),
    [
      explorerRestaurantListFetchLoading,
      isRestaurantsExplorer,
      jobLocationCoordinatesLoading,
      jobLocationPositionListLoading,
      restaurantLocationCoordinatesFetchLoading,
    ]
  );

  useEffect(() => {
    if (shouldLoad) {
      setExplorerLoading(true);
      setTimeout(() => setExplorerLoading(false), 1500);
    }
  }, [shouldLoad]);

  return explorerLoading;
};

const useFetchRestaurantExplorerParams = () => {
  const setActive = useSetAtom(toggleActiveExplorerTypeAtom);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const urlParams = new URLSearchParams(window.location.search);
    const explorerType = urlParams.get('explorer_type');
    if (explorerType) setActive(explorerType as ExplorerMapState);
  }, [setActive]);
};

const ListHeadTitleWrapper = ({
  hasMapView,
  className,
  ...props
}: { hasMapView?: boolean } & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex justify-between items-center gap-4 w-full pt-4 max-w-screen-2xl mx-auto',
      hasMapView ? 'lg:pt-0 lg:pr-2' : 'lg:pt-4 lg:px-4 2xl:px-0',
      className
    )}
    {...props}
  />
);
